import React, { useEffect, useState } from 'react';
import {
  Drawer, Box, Stack, Typography, IconButton, Divider,
  List, ListItem, ListItemText, ListItemIcon, CircularProgress,
  Chip, alpha, useTheme
} from '@mui/material';
import { Close, Star, ChevronRight } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { curriculumApi } from '../../services/sessions/curriculumApi';
import { subjectService } from '../../services/subjectService';
import { Subject } from '../../types';
import { CurriculumSubject } from '../../types/sessions';

interface EffectiveSubjectsPanelProps {
  open: boolean;
  sessionId: string;
  classId: string;
  sectionInstanceId: string;
  sectionName: string;
  className: string;
  onClose: () => void;
}

/**
 * Side panel: "what subjects does a student in this section actually sit for?"
 * Merges common + section-specific rows, badges each by origin.
 */
const EffectiveSubjectsPanel: React.FC<EffectiveSubjectsPanelProps> = ({
  open, sessionId, classId, sectionInstanceId, sectionName, className, onClose
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Array<{ origin: 'common' | 'section'; subject: Subject }>>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Pull both slices in parallel, then hydrate Subject names in one batch.
        const [common, specific, catalog] = await Promise.all([
          curriculumApi.list({ sessionId, classId, sectionInstanceId: null }),
          curriculumApi.list({ sessionId, classId, sectionInstanceId }),
          subjectService.getSubjects({ isActive: true, limit: 500 })
        ]);
        if (cancelled) return;
        const lookup = new Map<string, Subject>(catalog.subjects.map(s => [String(s._id), s]));
        const merged: Array<{ origin: 'common' | 'section'; subject: Subject }> = [];
        for (const r of common as CurriculumSubject[]) {
          const subj = lookup.get(String(r.subjectId));
          if (subj) merged.push({ origin: 'common', subject: subj });
        }
        for (const r of specific as CurriculumSubject[]) {
          const subj = lookup.get(String(r.subjectId));
          if (subj) merged.push({ origin: 'section', subject: subj });
        }
        setRows(merged);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not load subjects', { variant: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, classId, sectionInstanceId, enqueueSnackbar]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 } } } }}>
      <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
            Effective subjects
          </Typography>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Stack>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {className} · Section {sectionName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          What a student in this section will actually study.
        </Typography>
      </Box>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress size={24} /></Stack>
      ) : rows.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No subjects assigned yet for this class or section.
          </Typography>
        </Box>
      ) : (
        <Box>
          {(['common', 'section'] as const).map(group => {
            const groupRows = rows.filter(r => r.origin === group);
            if (groupRows.length === 0) return null;
            const isCommon = group === 'common';
            return (
              <Box key={group}>
                <Box sx={{
                  px: 2.5, py: 1.25,
                  bgcolor: alpha(isCommon ? theme.palette.warning.main : theme.palette.primary.main, 0.06)
                }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isCommon ? <Star sx={{ fontSize: 16, color: theme.palette.warning.dark }} />
                              : <ChevronRight sx={{ fontSize: 16, color: theme.palette.primary.dark }} />}
                    <Typography variant="overline" sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
                      {isCommon ? 'Common (all sections)' : `Section: ${sectionName}`}
                    </Typography>
                    <Chip size="small" variant="outlined" label={groupRows.length} sx={{ ml: 'auto', fontWeight: 600 }} />
                  </Stack>
                </Box>
                <List dense disablePadding>
                  {groupRows.map(({ subject }) => (
                    <ListItem key={subject._id} sx={{ pl: 3 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isCommon ? 'warning.main' : 'primary.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={subject.name}
                        secondary={subject.description}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Divider />
              </Box>
            );
          })}
        </Box>
      )}
    </Drawer>
  );
};

export default EffectiveSubjectsPanel;
